import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Button,
} from "@mui/material";
import Plot from "react-plotly.js";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

import {
  getPGPEMetricPlotLayout,
  getPGPEParcoordPlotLayout,
  getPGPEScatterPlotLayout,
} from "../../utils/plotting";

export default function OptimizationProgress(props) {
  const theme = useTheme();
  // init internationalization
  const { t } = useTranslation();

  // Create a DataFrame with dummy data
  const [selectedPoints, setSelectedPoints] = useState({});
  const [scatterData, setScatterData] = useState({});
  const [lineData, setLineData] = useState({});
  const [parcoordsData, setParcoordsData] = useState({});
  const [hasClickedReset, setHasClickedReset] = useState(true);
  const [currentSelectedPoint, setCurrentSelectedPoint] = useState(null);

  function updateSelectedPointsInPlots(selectedPoint) {
    setCurrentSelectedPoint(selectedPoint);
    // Helper function to update the selectedpoints attribute in the plots
    Object.keys(scatterData).forEach((k) => {
      // update selectedpoints for all traces
      scatterData[k].selectedpoints = [selectedPoint];
    });

    // Update the selected dimension in the parallel coordinates plot
    const newDimensions = [];
    parcoordsData.dimensions.forEach((dimension) => {
      const upperBound = props.plotData[dimension.label].newest[selectedPoint];
      const lowerBound =
        upperBound -
        0.01 * props.plotData[dimension.label].newest[selectedPoint];
      newDimensions.push({
        ...dimension,
        constraintrange: [lowerBound, upperBound],
      });
    });
    setParcoordsData({ ...parcoordsData, dimensions: newDimensions });
  }

  const handleScatterClick = (key, event) => {
    if (event.points.length > 0) {
      const selectedPoint = event.points[0].x;
      setSelectedPoints({ ...selectedPoints, [key]: selectedPoint });
      updateSelectedPointsInPlots(selectedPoint);
    }
    setHasClickedReset(false);
  };
  const handleClickBestEpoch = () => {
    setSelectedPoints({ ...selectedPoints, metric: props.statsData.bestEpoch });
    updateSelectedPointsInPlots(props.statsData.bestEpoch);
    setHasClickedReset(false);
  };

  const handleClickReset = () => {
    setHasClickedReset(true);
  };

  useEffect(() => {
    if (Object.values(props.plotData).length > 0) {
      const initialScatterData = {};
      const initialLineData = {};
      const initialSelectedPoints = {};
      // create index array of data to be used as initial selectedpoints
      const dataIndex = Array.from(
        { length: props.statsData.numTrials },
        (x, i) => i,
      );
      // get keys and sort them alphabetically
      Object.keys(props.plotData)
        .sort()
        .forEach((key) => {
          // set initial selected points depending on previous selection or reset
          if (currentSelectedPoint === null || hasClickedReset) {
            initialSelectedPoints[key] = dataIndex;
          } else {
            initialSelectedPoints[key] = [currentSelectedPoint];
          }

          initialScatterData[key] = {
            x: dataIndex,
            y: props.plotData[key].newest,
            mode: "markers",
            type: "scatter",
            marker: {
              size: 8,
              color: props.plotData.metric.newest,
              colorscale: "Bluered",
            },
            name:
              key === "metric"
                ? t("trainingStatus.Metric")
                : t("trainingStatus.Trial"),
            selectedpoints: initialSelectedPoints[key],
          };

          const bestLineTrace = {
            x: dataIndex,
            y: props.plotData[key].best,
            mode: "lines",
            type: "scatter",
            marker: { size: 8, color: theme.palette.secondary.main },
            name: t("trainingStatus.BestSoFar"),
            selectedpoints: initialSelectedPoints[key],
          };

          const lineTraces = [bestLineTrace];
          if (key === "metric" && props.plotData[key]?.baseline) {
            lineTraces.push({
              x: dataIndex,
              y: props.plotData[key].baseline,
              mode: "lines",
              type: "scatter",
              line: { color: theme.palette.success.main, dash: "dashdot" },
              name: "Baseline",
              selectedpoints: initialSelectedPoints[key],
            });
          }
          if (
            key!=="metric" &&
            props.plotData[key]?.mue &&
            props.plotData[key]?.sigma
          ) {
            const mueValues = props.plotData[key].mue;
            const sigmaValues = props.plotData[key].sigma;
            const sigmaLower = mueValues.map(
              (value, idx) => value - sigmaValues[idx],
            );
            const sigmaUpper = mueValues.map(
              (value, idx) => value + sigmaValues[idx],
            );

            lineTraces.push(
              {
                x: dataIndex,
                y: sigmaLower,
                mode: "lines",
                type: "scatter",
                line: { color: "rgba(0,0,0,0)" },
                name: "Mue - Sigma",
                showlegend: false,
                hoverinfo: "skip",
                selectedpoints: initialSelectedPoints[key],
              },
              {
                x: dataIndex,
                y: sigmaUpper,
                mode: "lines",
                type: "scatter",
                fill: "tonexty",
                fillcolor: "rgba(148, 88, 228, 0.2)",
                line: { color: "rgba(0,0,0,0)" },
                name: "Sigma",
                selectedpoints: initialSelectedPoints[key],
              },
              {
                x: dataIndex,
                y: mueValues,
                mode: "lines",
                type: "scatter",
                line: { color: theme.palette.info.main, dash: "dash" },
                name: "Mue",
                selectedpoints: initialSelectedPoints[key],
              },
            );
          }

          initialLineData[key] = lineTraces;
        });

      setLineData(initialLineData);
      setScatterData(initialScatterData);
      setSelectedPoints(initialSelectedPoints);

      const dimensionKeys = Object.keys(props.plotData);
      const sortedKeysWithoutMetric = dimensionKeys
        .filter((key) => key !== "metric")
        .sort();

      if (dimensionKeys.includes("metric")) {
        sortedKeysWithoutMetric.push("metric");
      }

      const dimensions = sortedKeysWithoutMetric.map((key) => ({
        label: key,
        values: props.plotData[key].newest,
      }));

      const initialParcoordsData = {
        line: {
          color: props.plotData.metric.newest,
          colorscale: "Bluered",
          showscale: true,
        },
        dimensions,
        selected: { line: { opacity: 1.0 } },
        unselected: { line: { opacity: 0.1 } },
        type: "parcoords",
      };

      setParcoordsData(initialParcoordsData);
    }
  }, [hasClickedReset, props.plotData]);

  return (
    <Grid
      id="obtimization-progress-container"
      container
      columnSpacing={4}
      sx={{ p: { xs: 2, sm: 3, md: 4, lg: 6, xl: 10 } }}
    >
      {
        // only render if current experiment has fulfilled all required steps and stats are loaded
        Object.values(props.plotData).length > 1 ? (
          <>
            <Grid item xs={12} sx={{ margin: theme.spacing(3, 0) }}>
              <Grid item xs={12}>
                <Grid
                  container
                  columnSpacing={3}
                  rowSpacing={3}
                  sx={{ alignItems: "left" }}
                >
                  <Grid item xs={12} sx={{ padding: theme.spacing(4, 1) }}>
                    <Typography variant="h4" gutterBottom>
                      {t("trainingStatus.OptimizerResults")}
                    </Typography>
                    <TableContainer sx={{ margin: theme.spacing(3, 0) }}>
                      <Table aria-label="table">
                        <TableHead>
                          <TableRow>
                            <TableCell>&nbsp;</TableCell>
                            {Object.keys(props.plotData)
                              .sort()
                              .map((key, index) => (
                                <TableCell
                                  key={`header-idx-${index}`}
                                  align="left"
                                >
                                  {/* Replace underscores with spaces and convert to uppercase */}
                                  {key
                                    .split("_")
                                    .map(
                                      (word) =>
                                        word.charAt(0).toUpperCase() +
                                        word.slice(1),
                                    )
                                    .join(" ")}
                                </TableCell>
                              ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow key="row-mu">
                            <TableCell align="left">
                              {t("trainingStatus.BestEpoch")}:{" "}
                              {props.statsData?.bestEpoch}
                              <br />
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={handleClickBestEpoch}
                              >
                                {t("trainingStatus.ShowBestEpochButton")}
                              </Button>
                            </TableCell>
                            {Object.keys(props.plotData)
                              .sort()
                              .map((key, index) => (
                                <TableCell
                                  align="left"
                                  key={`row-idx-${index}`}
                                >
                                  {props.plotData[key]?.best.at([
                                    props.statsData?.bestEpoch,
                                  ])}
                                </TableCell>
                              ))}
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                  <Grid item xs={12} sx={{ margin: theme.spacing(3, 0) }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleClickReset}
                    >
                      {t("trainingStatus.ResetPlotViewButton")}
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={6} key="metric">
                    <Box
                      sx={{
                        // width: "100%",
                        height: { xs: 360, sm: 400, md: 460, lg: 520 },
                      }}
                    >
                      <Plot
                        data={[scatterData.metric, ...(lineData.metric || [])]}
                        onClick={(event) => handleScatterClick("metric", event)}
                        layout={getPGPEMetricPlotLayout(
                          props.statsData?.numTrials,
                          t,
                        )}
                        useResizeHandler
                        style={{ width: "100%", height: "100%" }}
                        config={{ responsive: true }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        // width: "100%",
                        height: { xs: 360, sm: 400, md: 460, lg: 520 },
                      }}
                    >
                      <Plot
                        data={[parcoordsData]}
                        layout={getPGPEParcoordPlotLayout(t)}
                        useResizeHandler
                        style={{ width: "100%", height: "100%" }}
                        config={{ responsive: true }}
                      />
                    </Box>
                  </Grid>
                  {Object.keys(props.plotData)
                    .sort()
                    .map((key) =>
                      key !== "metric" ? (
                        <Grid item xs={12} md={6} key={key}>
                          <Box
                            sx={{
                              width: "100%",
                              height: { xs: 360, sm: 400, md: 460, lg: 520 },
                            }}
                          >
                            <Plot
                              data={[
                                scatterData[key],
                                ...(lineData[key] || []),
                              ]}
                              onClick={(event) =>
                                handleScatterClick(key, event)
                              }
                              layout={getPGPEScatterPlotLayout(
                                key,
                                props.statsData?.numTrials,
                                t,
                              )}
                              useResizeHandler
                              style={{ width: "100%", height: "100%" }}
                              config={{ responsive: true }}
                            />
                          </Box>
                        </Grid>
                      ) : (
                        ""
                      ),
                    )}
                </Grid>
              </Grid>
            </Grid>
          </>
        ) : (
          ""
        )
      }
    </Grid>
  );
}
